import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, Download, Landmark } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { balancesQueryOptions } from '@/features/bank-accounts/api'
import {
  TransactionDetailDrawer,
  type TransacaoDetalhe,
} from '@/features/bank-transactions/transaction-detail-drawer'
import { ScheduleActionDrawers } from '@/features/bills/schedule-action-drawers'
import { hojeIso } from '@/features/dashboard/datas'
import { linhasFuturas, type LinhaFutura } from '@/features/dashboard/fluxo-series'
import { janelaDeMeses } from '@/features/dashboard/resumo-series'
import { useSaldoFluxo } from '@/features/dashboard/use-saldo-fluxo'
import { IntegrationStatusBadge } from '@/features/integrations/components/integration-status-badge'
import { schedulesQueryOptions } from '@/features/schedules/api/queries'
import { formatBusinessDate } from '@/features/schedules/components/date-text'
import { MoneyText } from '@/features/schedules/components/money-text'
import type { ScheduleOut } from '@/shared/api/types'
import { baixarCsv } from '@/shared/lib/csv'
import { fromCents, isMoney, toCents, type Cents } from '@/shared/money/money'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { MonthRangePicker, type MesRange } from '@/shared/ui/month-range-picker'
import { PrintButton } from '@/shared/ui/print-button'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { LineChart, type ChartSeries } from '@/widgets/chart/line-chart'
import { PrintHeader } from '@/widgets/print/print-header'

export const Route = createFileRoute('/_authed/gestao-de-caixa/fluxo-de-caixa')({
  loader: ({ context }) => context.queryClient.ensureQueryData(balancesQueryOptions()),
  component: FluxoDeCaixaPage,
})

/** number decimal em centavos → tom pelo sinal (negativo = vermelho). */
function tonePorSinal(cents: number): 'expense' | 'neutral' {
  return cents < 0 ? 'expense' : 'neutral'
}

function FluxoDeCaixaPage() {
  const { t, i18n } = useTranslation(['reports', 'nav', 'common', 'bankAccounts'])
  const lang = i18n.language
  // Faixa de meses do gráfico (seletor estilo Nibo). Default = mês atual; nasce de
  // `hoje` a cada carga, então recarregar sempre volta ao mês corrente.
  const mesAtual = hojeIso().slice(0, 7)
  const [rangeMes, setRangeMes] = useState<MesRange>({ from: mesAtual, to: mesAtual })
  const janelaFluxo = useMemo(
    () => janelaDeMeses(rangeMes.from, rangeMes.to),
    [rangeMes.from, rangeMes.to],
  )

  // Detalhe ao clicar na descrição: agendamento CONTÁBIL (drawer com ações, via
  // id) ou transação da INTEGRAÇÃO (Hatch → detalhe read-only, sem ações).
  const [scheduleDetalhe, setScheduleDetalhe] = useState<{
    id: string
    kind: 'payable' | 'receivable'
  } | null>(null)
  const [transacaoDetalhe, setTransacaoDetalhe] = useState<TransacaoDetalhe | null>(null)
  const abrirDetalheFuturo = (l: LinhaFutura) => {
    if (l.opening || l.kind === null) return
    if (l.origem === 'integracao') {
      setTransacaoDetalhe({
        name: l.name,
        description: l.description,
        dueDate: l.dueDate ?? '',
        kind: l.kind,
        amount: l.amount ?? '0.00',
        // Linha FUTURA (aberta) — não há liquidado nem baixa; drawer cai no nominal.
        settledAmount: null,
        settledOn: null,
        status: l.status,
      })
    } else {
      setScheduleDetalhe({ id: l.key, kind: l.kind })
    }
  }

  // Contas (manuais + integrações), seleção por checkbox, saldo e série do
  // gráfico — a MESMA lógica da Resumo (hook compartilhado, para não divergir).
  const {
    hojeIsoStr,
    janela,
    contasManuais,
    integradas,
    estaMarcada,
    alternar,
    saldoIntegracao,
    refPorConta,
    saldoHojeCents,
    chartPoints,
    futurosCombinados,
    graficoCarregando,
    balancesLoading,
  } = useSaldoFluxo(janelaFluxo)

  // Agendamentos já BAIXADOS no período (tabela "Passados", específica do Fluxo).
  const passadosQ = useQuery(
    schedulesQueryOptions({ status: 'paid', order_by: '-due_date', limit: 200 }),
  )
  type Baixado = ScheduleOut & { entry: NonNullable<ScheduleOut['entry']> }
  const passadosJanela = useMemo(
    () =>
      (passadosQ.data?.items ?? [])
        .filter(
          (s): s is Baixado =>
            s.entry !== null && s.entry.paid_date >= janela.from && s.entry.paid_date <= hojeIsoStr,
        )
        .sort((a, b) => a.entry.paid_date.localeCompare(b.entry.paid_date)),
    [passadosQ.data, janela.from, hojeIsoStr],
  )

  const linhasFut = linhasFuturas(saldoHojeCents, futurosCombinados)

  const dinheiroCompacto = (cents: number) =>
    new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(cents / 100)

  const series: readonly ChartSeries[] = [
    {
      id: 'saldo',
      label: t('reports:resumo.balanceLine'),
      color: 'var(--color-action)',
      // Abaixo de zero a linha/área viram vermelhas (saldo negativo é comum: o a
      // receber é de longo prazo). Corte exato no eixo zero.
      negativeColor: 'var(--color-expense)',
      area: true,
      points: chartPoints,
    },
  ]

  // Exporta os agendamentos (Passados + Futuros) da janela como planilha — as
  // colunas do Nibo: Data · Vencimento · Nome · Descrição · Entrada · Saída. O
  // valor cai em Entrada (receber) ou Saída (pagar); decimal com vírgula (pt-BR).
  const dinheiroCsv = (v: string | null | undefined) =>
    v !== null && v !== undefined && isMoney(v) ? v.replace('.', ',') : ''
  const linhaCsv = (
    data: string,
    dueDate: string,
    nome: string | null,
    descricao: string | null,
    kind: 'payable' | 'receivable',
    amount: string,
  ): string[] => [
    formatBusinessDate(data, lang),
    formatBusinessDate(dueDate, lang),
    nome ?? '',
    descricao ?? '',
    kind === 'receivable' ? dinheiroCsv(amount) : '',
    kind === 'payable' ? dinheiroCsv(amount) : '',
  ]
  const exportarAgendamentos = () => {
    const linhas: string[][] = [
      [
        t('reports:flow.columns.date'),
        t('reports:flow.columns.dueDate'),
        t('reports:flow.columns.name'),
        t('reports:flow.columns.description'),
        t('reports:flow.columns.inflow'),
        t('reports:flow.columns.outflow'),
      ],
      ...passadosJanela.map((s) =>
        linhaCsv(
          s.entry.paid_date,
          s.due_date,
          s.stakeholder.name,
          s.description,
          s.kind,
          s.amount,
        ),
      ),
      ...futurosCombinados.map((f) =>
        linhaCsv(f.date, f.dueDate, f.name, f.description, f.kind, f.amount),
      ),
    ]
    baixarCsv(`fluxo-de-caixa-${rangeMes.from}_a_${rangeMes.to}.csv`, linhas)
  }

  return (
    <>
      <PageHeader
        title={t('nav:cash.flow')}
        actions={
          <>
            <Button
              variant="ghost"
              size="icon"
              data-print-hide
              aria-label={t('reports:flow.exportCsv')}
              onClick={exportarAgendamentos}
            >
              <Download aria-hidden />
            </Button>
            <PrintButton />
          </>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        <PrintHeader title={t('nav:cash.flow')} />
        <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
          {/* Contas com checkbox + saldo das marcadas. */}
          <Card className="flex flex-col overflow-hidden">
            {balancesLoading ? (
              <div className="flex flex-col gap-2 p-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : (
              <ul className="divide-line flex-1 divide-y">
                {contasManuais.map((s) => (
                  <li key={s.bank_account.id} className="flex items-center gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={estaMarcada(s.bank_account.id)}
                      onChange={() => {
                        alternar(s.bank_account.id)
                      }}
                      aria-label={s.bank_account.name}
                      className="size-4 shrink-0 rounded"
                      style={{ accentColor: 'var(--color-action)' }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink truncate text-sm">{s.bank_account.name}</p>
                      {refPorConta.has(s.bank_account.id) && (
                        <p className="text-ink-subtle truncate text-xs">
                          {refPorConta.get(s.bank_account.id)}
                        </p>
                      )}
                    </div>
                    <MoneyText
                      value={s.balance}
                      emphasis
                      tone={isMoney(s.balance) ? tonePorSinal(toCents(s.balance)) : 'neutral'}
                    />
                  </li>
                ))}
                {/* Integrações (Hatch etc.) = contas: checkbox + saldo REALIZADO
                    (transações baixadas). O a-receber em aberto entra na projeção. */}
                {integradas.map((i, idx) => {
                  const saldo = saldoIntegracao(idx)
                  return (
                    <li key={i.id} className="flex items-center gap-3 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={estaMarcada(i.id)}
                        onChange={() => {
                          alternar(i.id)
                        }}
                        aria-label={i.name}
                        className="size-4 shrink-0 rounded"
                        style={{ accentColor: 'var(--color-action)' }}
                      />
                      <Landmark className="text-ink-subtle size-4 shrink-0" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="text-ink truncate text-sm">{i.name}</p>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <Badge tone="info">{t('bankAccounts:nature.integrated')}</Badge>
                          <IntegrationStatusBadge status={i.status} />
                        </div>
                      </div>
                      <MoneyText
                        value={fromCents(saldo as Cents)}
                        emphasis
                        tone={tonePorSinal(saldo)}
                      />
                    </li>
                  )
                })}
                {contasManuais.length === 0 && integradas.length === 0 && (
                  <li className="text-ink-subtle px-4 py-8 text-center text-sm">
                    {t('bankAccounts:empty.title')}
                  </li>
                )}
              </ul>
            )}
            <div className="border-line bg-surface-2 flex items-center justify-between border-t px-4 py-3">
              <span className="text-ink-muted text-sm">{t('reports:resumo.balanceTotal')}</span>
              <MoneyText
                value={fromCents(saldoHojeCents as Cents)}
                emphasis
                tone={tonePorSinal(saldoHojeCents)}
              />
            </div>
          </Card>

          {/* Gráfico de saldo com navegação de período. */}
          <Card className="flex flex-col">
            <div className="flex items-center justify-end px-5 py-3">
              <MonthRangePicker
                value={rangeMes}
                onChange={setRangeMes}
                ariaLabel={t('nav:cash.flow')}
              />
            </div>
            <div className="px-5 pb-4">
              {graficoCarregando ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <LineChart
                  series={series}
                  ariaLabel={t('reports:resumo.chartAria')}
                  formatValue={dinheiroCompacto}
                  formatDate={(iso) => formatBusinessDate(iso, lang)}
                  todayX={hojeIsoStr}
                  todayLabel={t('reports:resumo.today')}
                  height={256}
                />
              )}
            </div>
          </Card>
        </div>

        <PassadosCard
          rows={passadosJanela}
          loading={passadosQ.isLoading}
          onSelect={(s) => {
            setScheduleDetalhe({ id: s.id, kind: s.kind })
          }}
        />
        <FuturosCard linhas={linhasFut} loading={false} onSelect={abrirDetalheFuturo} />
      </div>

      {/* Detalhe do contábil (busca por id → drawer com ações) e da integração
          (Hatch → read-only). Só um por vez; fechar volta ao estado nulo. */}
      {scheduleDetalhe !== null && (
        <ScheduleActionDrawers
          scheduleId={scheduleDetalhe.id}
          acao="detail"
          kind={scheduleDetalhe.kind}
          onClose={() => {
            setScheduleDetalhe(null)
          }}
        />
      )}
      <TransactionDetailDrawer
        dados={transacaoDetalhe}
        onOpenChange={(o) => {
          if (!o) setTransacaoDetalhe(null)
        }}
      />
    </>
  )
}

/** Cabeçalho de coluna com seta colorida (entrada/saída). */
function ColSeta({ dir, children }: { dir: 'in' | 'out'; children: string }) {
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {dir === 'in' ? (
        <ArrowUp className="text-income size-3" aria-hidden />
      ) : (
        <ArrowDown className="text-expense size-3" aria-hidden />
      )}
      {children}
    </span>
  )
}

const TH = 'px-4 py-2 text-left align-middle font-medium'
const TD = 'px-4 py-2 align-middle'

/** Agendamentos Passados: já baixados no período, sem saldo corrente. */
function PassadosCard({
  rows,
  loading,
  onSelect,
}: {
  rows: readonly (ScheduleOut & { entry: NonNullable<ScheduleOut['entry']> })[]
  loading: boolean
  onSelect: (s: ScheduleOut) => void
}) {
  const { t, i18n } = useTranslation(['reports'])
  const lang = i18n.language
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-ink text-sm font-semibold">{t('reports:flow.pastSchedules')}</h2>
      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        ) : (
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="bg-surface-2 text-ink-muted text-xs">
                <th className={TH}>{t('reports:flow.columns.date')}</th>
                <th className={TH}>{t('reports:flow.columns.dueDate')}</th>
                <th className={TH}>{t('reports:flow.columns.name')}</th>
                <th className={TH}>{t('reports:flow.columns.description')}</th>
                <th className={`${TH} text-right`}>
                  <ColSeta dir="in">{t('reports:flow.columns.inflow')}</ColSeta>
                </th>
                <th className={`${TH} text-right`}>
                  <ColSeta dir="out">{t('reports:flow.columns.outflow')}</ColSeta>
                </th>
              </tr>
            </thead>
            <tbody className="divide-line divide-y">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-ink-subtle px-4 py-6 text-center">
                    {t('reports:flow.empty')}
                  </td>
                </tr>
              ) : (
                rows.map((s) => {
                  const income = s.kind === 'receivable'
                  return (
                    <tr key={s.id}>
                      <td className={`${TD} tnum text-ink-muted`}>
                        {formatBusinessDate(s.entry.paid_date, lang)}
                      </td>
                      <td className={`${TD} tnum text-expense`}>
                        {formatBusinessDate(s.due_date, lang)}
                      </td>
                      <td className={`${TD} text-ink`}>{s.stakeholder.name}</td>
                      <td className={TD}>
                        <button
                          type="button"
                          onClick={() => {
                            onSelect(s)
                          }}
                          className="text-action text-left hover:underline"
                        >
                          {s.description ?? '—'}
                        </button>
                      </td>
                      <td className={`${TD} text-right`}>
                        {income ? <MoneyText value={s.amount} tone="income" /> : null}
                      </td>
                      <td className={`${TD} text-right`}>
                        {income ? null : <MoneyText value={s.amount} tone="expense" />}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  )
}

/** Agendamentos Futuros: abre em "Saldo anterior" e mostra o saldo corrente. */
function FuturosCard({
  linhas,
  loading,
  onSelect,
}: {
  linhas: ReturnType<typeof linhasFuturas>
  loading: boolean
  onSelect: (l: LinhaFutura) => void
}) {
  const { t, i18n } = useTranslation(['reports'])
  const lang = i18n.language
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-ink text-sm font-semibold">{t('reports:flow.futureSchedules')}</h2>
      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        ) : (
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="bg-surface-2 text-ink-muted text-xs">
                <th className={TH}>{t('reports:flow.columns.date')}</th>
                <th className={TH}>{t('reports:flow.columns.dueDate')}</th>
                <th className={TH}>{t('reports:flow.columns.name')}</th>
                <th className={TH}>{t('reports:flow.columns.description')}</th>
                <th className={`${TH} text-right`}>
                  <ColSeta dir="in">{t('reports:flow.columns.inflow')}</ColSeta>
                </th>
                <th className={`${TH} text-right`}>
                  <ColSeta dir="out">{t('reports:flow.columns.outflow')}</ColSeta>
                </th>
                <th className={`${TH} text-right`}>{t('reports:flow.columns.balance')}</th>
              </tr>
            </thead>
            <tbody className="divide-line divide-y">
              {linhas.map((l) => {
                const income = l.kind === 'receivable'
                return (
                  <tr key={l.key} className={l.opening ? 'bg-surface-2/40' : undefined}>
                    <td className={`${TD} tnum text-ink-muted`}>
                      {l.opening ? null : l.date ? formatBusinessDate(l.date, lang) : null}
                    </td>
                    <td className={`${TD} tnum text-ink-muted`}>
                      {l.dueDate ? formatBusinessDate(l.dueDate, lang) : null}
                    </td>
                    <td className={`${TD} text-ink`}>{l.name}</td>
                    <td className={TD}>
                      {l.opening ? (
                        <span className="text-ink-muted">{t('reports:flow.previousBalance')}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            onSelect(l)
                          }}
                          className="text-action text-left hover:underline"
                        >
                          {l.description ?? '—'}
                        </button>
                      )}
                    </td>
                    <td className={`${TD} text-right`}>
                      {!l.opening && income ? <MoneyText value={l.amount} tone="income" /> : null}
                    </td>
                    <td className={`${TD} text-right`}>
                      {!l.opening && l.kind === 'payable' ? (
                        <MoneyText value={l.amount} tone="expense" />
                      ) : null}
                    </td>
                    <td className={`${TD} text-right`}>
                      <MoneyText
                        value={fromCents(l.saldoCents as Cents)}
                        emphasis={l.opening}
                        tone={tonePorSinal(l.saldoCents)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  )
}
